/**
 * @module @shared/config/query-keys
 * Zentrale Query-Key-Factory für die gesamte Admin-App. Jede Funktion liefert
 * ein `as const`-Tupel, sodass Invalidierung und Cache-Zugriff konsistent bleiben.
 * Entities (Lese-`queryOptions`) und Features (Mutationen/Invalidierung) leiten
 * ihre Keys AUSSCHLIESSLICH von hier ab — niemals Strings inline duplizieren.
 *
 * Konvention je Domäne:
 *   all()           → Wurzel-Key der Domäne (für globale Invalidierung)
 *   lists()         → alle Listen
 *   list(filters?)  → konkrete (gefilterte) Liste
 *   details()       → alle Detail-Einträge
 *   detail(id)      → konkreter Eintrag
 */

/** Generischer, optionaler Filter-Parameter für Listen-Keys. */
export type QueryFilters = Record<string, unknown> | undefined;

/** Query-Keys für registrierte Apps (`identity.apps`). */
const apps = {
  all: () => ['apps'] as const,
  lists: () => [...apps.all(), 'list'] as const,
  list: (filters?: QueryFilters) => [...apps.lists(), filters ?? {}] as const,
  details: () => [...apps.all(), 'detail'] as const,
  detail: (id: string) => [...apps.details(), id] as const,
  /** Redirect-Ziele einer App (`identity.app_redirects`). */
  redirects: (appId: string) => [...apps.detail(appId), 'redirects'] as const,
};

/** Query-Keys für Rollen (`identity.roles`). */
const roles = {
  all: () => ['roles'] as const,
  lists: () => [...roles.all(), 'list'] as const,
  list: (filters?: QueryFilters) => [...roles.lists(), filters ?? {}] as const,
  details: () => [...roles.all(), 'detail'] as const,
  detail: (id: string) => [...roles.details(), id] as const,
};

/** Query-Keys für Berechtigungen (`identity.permissions`). */
const permissions = {
  all: () => ['permissions'] as const,
  lists: () => [...permissions.all(), 'list'] as const,
  list: (filters?: QueryFilters) => [...permissions.lists(), filters ?? {}] as const,
};

/** Query-Keys für App-Freischaltungen / Grants (`identity.user_app_access`). */
const grants = {
  all: () => ['grants'] as const,
  lists: () => [...grants.all(), 'list'] as const,
  list: (filters?: QueryFilters) => [...grants.lists(), filters ?? {}] as const,
  details: () => [...grants.all(), 'detail'] as const,
  detail: (id: string) => [...grants.details(), id] as const,
};

/** Query-Keys für Nutzer (`auth.users` via Admin-Sicht/Edge-Function). */
const users = {
  all: () => ['users'] as const,
  lists: () => [...users.all(), 'list'] as const,
  list: (filters?: QueryFilters) => [...users.lists(), filters ?? {}] as const,
  details: () => [...users.all(), 'detail'] as const,
  detail: (id: string) => [...users.details(), id] as const,
};

/** Query-Keys für den Audit-Trail (`identity.audit_log`). */
const audit = {
  all: () => ['audit'] as const,
  lists: () => [...audit.all(), 'list'] as const,
  list: (filters?: QueryFilters) => [...audit.lists(), filters ?? {}] as const,
};

/**
 * Query-Keys für aufgelöste Nutzerprofile (`auth.users` + `identity.user_profiles`,
 * via Identity-Admin-Worker). Trennt Suche (`list(query)`), Einzel-Auflösung
 * (`detail(id)`) und Batch-Auflösung (`resolve(ids)`).
 */
const userProfiles = {
  all: () => ['userProfiles'] as const,
  lists: () => [...userProfiles.all(), 'list'] as const,
  /** Konkrete (Such-)Liste — `query` ist Teil des Keys (leer = neueste Nutzer). */
  list: (query: string) => [...userProfiles.lists(), query] as const,
  details: () => [...userProfiles.all(), 'detail'] as const,
  detail: (id: string) => [...userProfiles.details(), id] as const,
  /**
   * Batch-Auflösung mehrerer UUIDs. Die IDs werden sortiert in den Key
   * aufgenommen, sodass identische Mengen unabhängig von der Reihenfolge
   * denselben (stabilen) Cache-Eintrag teilen.
   */
  resolve: (ids: string[]) =>
    [...userProfiles.all(), 'resolve', [...ids].sort().join(',')] as const,
};

/**
 * Query-Keys für die zentralen Rechtstext-Bausteine (Schema `legal`, ADR-0010).
 * Eine gemeinsame Wurzel `['legal']` für alle vier Tabellen, damit eine Merge-Aktion
 * oder eine Standard-Änderung mit EINER Invalidierung Bausteine, Historie, Profile
 * und Overrides zugleich frisch zieht — sie hängen fachlich voneinander ab
 * (`override.baseVersion < block.version`).
 */
const legal = {
  all: () => ['legal'] as const,
  /** Der Standard-Katalog (`legal.blocks`). */
  blocks: () => [...legal.all(), 'blocks'] as const,
  /** Die Versionshistorie (`legal.block_versions`) — base-Seite des 3-Wege-Merge. */
  blockVersions: () => [...legal.all(), 'blockVersions'] as const,
  profiles: () => [...legal.all(), 'profile', 'list'] as const,
  profile: (appId: string) => [...legal.all(), 'profile', 'detail', appId] as const,
  /** Overrides aller Apps (`appId` weggelassen) oder einer App. */
  overrides: (appId?: string) => [...legal.all(), 'override', 'list', appId ?? null] as const,
};

/** Gesamte Query-Key-Factory der Admin-App. */
export const queryKeys = {
  apps,
  roles,
  permissions,
  grants,
  users,
  audit,
  userProfiles,
  legal,
} as const;
